import * as React from "react";

import {
  isTopicStatsRefusal,
  sessionTopicFieldRows,
  type SessionTopicRecord,
  type SessionTopicSchema,
  type SessionTopicStatsRecord,
} from "@/shared/domain/sessions";
import { useSessionTopicStats } from "@/shared/domain/sessions/hooks";
import { useSchemaFields } from "@/shared/domain/topics/hooks";
import { Banner, Select, Spinner } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";

import styles from "./SessionTopicPanel.module.css";
import { TopicFieldStatsTable } from "./TopicFieldStatsTable";

export interface SessionTopicPanelProps {
  topic: SessionTopicRecord;
  sessionId: string;
  orgId: string;
  /** Whether the Topics tab shows topic stats for the session. */
  statsShown: boolean;
  /** Whether a topic stats run for the session is scheduled or running. */
  generating: boolean;
}

/**
 * The expanded row of a session topic: its schema's fields and, while
 * `statsShown` holds and the stats route does not refuse the session's org,
 * their stats. Fields whose stats fail to load are listed without them, under
 * the error. A topic recorded with several schemas gets a picker; stats are
 * per path across every schema, so switching schemas changes the field list
 * but not a path's numbers. The fields sit on the detail row's own background,
 * framed only by a line down their left edge.
 */
export function SessionTopicPanel({
  topic,
  sessionId,
  orgId,
  statsShown,
  generating,
}: SessionTopicPanelProps) {
  const [schemaId, setSchemaId] = React.useState(
    topic.schemas[0]?.schema_id ?? null,
  );
  const schema =
    topic.schemas.find((candidate) => candidate.schema_id === schemaId) ??
    topic.schemas[0] ??
    null;

  return (
    <div className={styles.root}>
      <div className={styles.frame}>
        {topic.schemas.length > 1 && schema !== null && (
          <Select
            aria-label="Schema"
            size="sm"
            className={styles.schemaPicker}
            items={topic.schemas.map((candidate) => ({
              value: candidate.schema_id,
              label: candidate.name ?? candidate.schema_id,
            }))}
            value={schema.schema_id}
            onValueChange={(next) => {
              if (next !== null) {
                setSchemaId(next);
              }
            }}
          />
        )}
        {schema === null ? (
          <p className={styles.secondary}>No schema recorded</p>
        ) : (
          <SchemaFields
            key={schema.schema_id}
            topic={topic}
            schema={schema}
            sessionId={sessionId}
            orgId={orgId}
            requestStats={statsShown}
            generating={generating}
          />
        )}
      </div>
    </div>
  );
}

const NO_STATS: Pick<SessionTopicStatsRecord, "fields"> = { fields: [] };

function SchemaFields({
  topic,
  schema,
  sessionId,
  orgId,
  requestStats,
  generating,
}: {
  topic: SessionTopicRecord;
  schema: SessionTopicSchema;
  sessionId: string;
  orgId: string;
  requestStats: boolean;
  generating: boolean;
}) {
  const fieldsQuery = useSchemaFields(schema.schema_id, orgId);
  const statsQuery = useSessionTopicStats(sessionId, topic.topic_id, {
    enabled: requestStats,
  });
  // The stats route can refuse the session's org before the run route has.
  const statsReleased = requestStats && !isTopicStatsRefusal(statsQuery.error);
  const statsError = statsReleased ? statsQuery.error : null;
  const statsShown = statsReleased && statsError === null;

  if (fieldsQuery.error) {
    return (
      <Banner variant="error" className={styles.banner}>
        {userFacingErrorMessage(
          fieldsQuery.error,
          "Failed to load this topic's fields.",
        )}
      </Banner>
    );
  }
  const stats = statsShown ? statsQuery.data : NO_STATS;
  if (fieldsQuery.data === undefined || stats === undefined) {
    return <Spinner aria-label="Loading fields" className={styles.spinner} />;
  }
  const rows = sessionTopicFieldRows(schema.name, fieldsQuery.data, stats);

  return (
    <>
      {statsError && (
        <Banner variant="error" className={styles.banner}>
          {userFacingErrorMessage(
            statsError,
            "Failed to load this topic's stats.",
          )}
        </Banner>
      )}
      <TopicFieldStatsTable
        rows={rows}
        statsShown={statsShown}
        generating={generating}
      />
    </>
  );
}
